import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';

import type { AdMediaType, AdModel } from '../../types/ads';
import type { AiListingAutofillResponse } from '../../types/aiListingAutofill';
import { FileKindEnum, UserKindEnum } from '../../types/enums';
import type {
  PropertyAdEditorFormValues,
  PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';
import type { State } from '../../types/states';

import { SectionHeader } from '../uiComponents';
import { AiListingReviewProvider, AiReviewBanner, AiReviewFieldNotice } from './AiListingReview';
import { CommissionConditionsSection } from './CommissionConditionsSection';
import { DescriptionSection } from './DescriptionSection';
import { FeaturesAmenitiesSection } from './FeaturesAmenitiesSection';
import { ListingPreview } from './ListingPreview';
import { PricingTransactionSection } from './PricingTransactionSection';
import { PropertyAdEditorActions } from './PropertyAdEditorActions';
import { PropertyDetailsSection } from './PropertyDetailsSection';
import { PropertyLocationSection } from './PropertyLocationSection';
import { PropertySpecificationsSection } from './PropertySpecificationsSection';
import { ResponsibleUserSection } from './ResponsibleUserSection';
import { UploadPhotoSection } from './UploadPhotoSection';
import { UploadVideoSection } from './UploadVideoSection';
import { mapPropertyAdToEditorForm } from './propertyAdEditorEdit';
import { mapWorkspaceToEditorForm } from './propertyAdEditorForm';
import styles from './PropertyAdEditor.module.scss';
import {
  usePropertyAdEditorAutosave,
  type PropertyAdEditorAutosaveState,
} from './usePropertyAdEditorAutosave';
import {
  usePropertyAdEditorMedia,
  type PropertyAdEditorMediaIdleResult,
} from './usePropertyAdEditorMedia';

interface PropertyAdEditorCreateProps {
  mode?: 'create';
  workspace: PropertyAdInProgressWorkspace;
  aiReview?: AiListingAutofillResponse['review'];
  aiLocationStatus?: AiListingAutofillResponse['location']['status'];
}

interface PropertyAdEditorEditProps {
  mode: 'edit';
  propertyAd: AdModel;
}

type PropertyAdEditorProps = PropertyAdEditorCreateProps | PropertyAdEditorEditProps;

type EditorResource = Pick<
  PropertyAdInProgressWorkspace,
  'id' | 'propertyAdCreator' | 'propertyAdMedia' | 'userId'
>;

interface AutosavePresentation {
  reloadSavedVersion: () => void;
  retry: () => void;
  state: PropertyAdEditorAutosaveState;
}

interface PropertyAdEditorLayoutProps {
  actions: (
    media: AdMediaType[],
    onPhotoErrorChange: (message: string) => void,
    finalization: {
      hasPendingMedia: () => boolean;
      onFinalizingChange: (isFinalizing: boolean) => void;
      waitForMediaIdle: () => Promise<PropertyAdEditorMediaIdleResult>;
    }
  ) => ReactNode;
  autosave?: AutosavePresentation;
  aiReview?: AiListingAutofillResponse['review'];
  aiLocationStatus?: AiListingAutofillResponse['location']['status'];
  formMethods: UseFormReturn<PropertyAdEditorFormValues>;
  initialValues: PropertyAdEditorFormValues;
  isAgency: boolean;
  resetKey: string;
  resource: EditorResource;
}

const AutosaveStatus = ({ reloadSavedVersion, retry, state }: AutosavePresentation) => {
  const label = {
    disabled: '',
    error: "Couldn't save changes",
    conflict: "Couldn't save changes",
    saved: 'Saved',
    saving: 'Saving...',
  }[state.status];

  if (state.status === 'disabled') return null;

  return (
    <Stack
      alignItems='center'
      aria-live='polite'
      direction='row'
      justifyContent='flex-end'
      marginTop={1}
      minHeight={24}
      spacing={1.5}
    >
      <Typography
        color='#6f7d87'
        component='span'
        fontWeight={400}
        role={state.status === 'error' || state.status === 'conflict' ? 'alert' : 'status'}
        variant='body2'
      >
        {label}
      </Typography>
      {state.status === 'error' && (
        <Button
          color='primary'
          onClick={retry}
          size='small'
          sx={{ minWidth: 0, padding: 0, textTransform: 'none' }}
          type='button'
          variant='text'
        >
          Retry
        </Button>
      )}
      {state.status === 'conflict' && (
        <Button
          disabled={!state.canReload}
          onClick={() => void reloadSavedVersion()}
          size='small'
          sx={{ minWidth: 0, padding: 0, textTransform: 'none' }}
          type='button'
          variant='text'
        >
          Reload saved version
        </Button>
      )}
    </Stack>
  );
};

const PropertyAdEditorLayout = ({
  actions,
  autosave,
  aiReview,
  aiLocationStatus,
  formMethods,
  initialValues,
  isAgency,
  resetKey,
  resource,
}: PropertyAdEditorLayoutProps) => {
  const [photoPublishError, setPhotoPublishError] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const editorControlsRef = useRef<HTMLFieldSetElement>(null);
  const {
    beginUpload,
    finishUpload,
    hasPendingMedia,
    media: propertyAdMedia,
    mediaWorkspace,
    persistPhotos,
    persistVideos,
    waitForMediaIdle,
  } = usePropertyAdEditorMedia(resource);

  useLayoutEffect(() => {
    editorControlsRef.current?.toggleAttribute('inert', isFinalizing);
  }, [isFinalizing]);

  return (
    <Box className={styles.editorFrame} component='section' data-workspace-id={resource.id}>
      <SectionHeader title='PropertyAd Editor' titleComponent='h1' />
      {autosave && <AutosaveStatus {...autosave} />}
      <Box aria-label='PropertyAd Editor workspace' paddingY={{ xs: 3, sm: 4 }}>
        <AiListingReviewProvider
          key={resource.id}
          locationStatus={aiLocationStatus}
          review={aiReview}
        >
          <FormProvider {...formMethods}>
            <Box className={styles.editorLayout} data-testid='property-ad-editor-layout'>
              <Box data-testid='property-ad-editor-form-column' minWidth={0} width='100%'>
                <Box
                  component='fieldset'
                  disabled={isFinalizing}
                  ref={editorControlsRef}
                  sx={{
                    border: 0,
                    margin: 0,
                    minWidth: 0,
                    padding: 0,
                    pointerEvents: isFinalizing ? 'none' : 'auto',
                  }}
                >
                  <Stack
                    aria-label='PropertyAd Editor form'
                    component='form'
                    noValidate
                    spacing={{ xs: 3, sm: 4 }}
                  >
                    <AiReviewBanner />
                    <AiReviewFieldNotice target='section:general' />
                    <PropertyDetailsSection />
                    <PricingTransactionSection />
                    <PropertyLocationSection
                      key={resetKey}
                      initialAddress={initialValues.addressEn ?? ''}
                      initialIsNewLocation={
                        !initialValues.locationId &&
                        Boolean(initialValues.districtId || initialValues.buildingName)
                      }
                    />
                    <CommissionConditionsSection />
                    <PropertySpecificationsSection />
                    <FeaturesAmenitiesSection />
                    <DescriptionSection />
                    <UploadPhotoSection
                      disabled={isFinalizing}
                      onUploadComplete={(ok) => finishUpload(FileKindEnum.Image, ok)}
                      onUploadStart={beginUpload}
                      persistPhotoMedia={persistPhotos}
                      publishError={photoPublishError}
                      workspace={mediaWorkspace}
                    />
                    <UploadVideoSection
                      disabled={isFinalizing}
                      onUploadComplete={(ok) => finishUpload(FileKindEnum.Video, ok)}
                      onUploadStart={beginUpload}
                      persistVideoMedia={persistVideos}
                      workspace={mediaWorkspace}
                    />
                    {isAgency && <ResponsibleUserSection workspace={resource} />}
                    {actions(propertyAdMedia, setPhotoPublishError, {
                      hasPendingMedia,
                      onFinalizingChange: setIsFinalizing,
                      waitForMediaIdle,
                    })}
                  </Stack>
                </Box>
              </Box>
              <Box
                aria-label='Listing Preview column'
                className={styles.previewColumn}
                component='aside'
                data-testid='property-ad-editor-preview-column'
                minWidth={0}
                width='100%'
              >
                <ListingPreview media={propertyAdMedia} />
              </Box>
            </Box>
          </FormProvider>
        </AiListingReviewProvider>
      </Box>
    </Box>
  );
};

const CreateLifecycle = ({
  aiLocationStatus,
  aiReview,
  workspace,
}: PropertyAdEditorCreateProps) => {
  const isAgency = useSelector(({ auth }: State) => auth.user.kind === UserKindEnum.Agency);
  const responsibleDefaultUserId = isAgency ? workspace.createdByUserId : undefined;
  const initialValues = useMemo(
    () => mapWorkspaceToEditorForm(workspace, responsibleDefaultUserId),
    [responsibleDefaultUserId, workspace]
  );
  const formMethods = useForm<PropertyAdEditorFormValues>({
    defaultValues: initialValues,
    shouldUnregister: false,
  });

  useEffect(() => {
    formMethods.reset(initialValues);
  }, [formMethods, initialValues]);

  const { flush, reloadSavedVersion, retry, state, stopAutosave } = usePropertyAdEditorAutosave({
    control: formMethods.control,
    getValues: formMethods.getValues,
    initialValues,
    responsibleDefaultUserId,
    reset: formMethods.reset,
    workspace,
  });

  return (
    <PropertyAdEditorLayout
      aiLocationStatus={aiLocationStatus}
      aiReview={aiReview}
      actions={(media, onPhotoPublishErrorChange, finalization) => (
        <PropertyAdEditorActions
          {...finalization}
          flushAutosave={flush}
          media={media}
          onPhotoPublishErrorChange={onPhotoPublishErrorChange}
          stopAutosave={stopAutosave}
          workspace={workspace}
        />
      )}
      autosave={{ reloadSavedVersion, retry, state }}
      formMethods={formMethods}
      initialValues={initialValues}
      isAgency={isAgency}
      resetKey={`${workspace.id}:${workspace.version}`}
      resource={workspace}
    />
  );
};

const EditLifecycle = ({ propertyAd }: PropertyAdEditorEditProps) => {
  const currentUser = useSelector(({ auth }: State) => auth.user);
  const isAgency = currentUser.kind === UserKindEnum.Agency;
  const responsibleDefaultUserId = isAgency ? currentUser.id : undefined;
  const initialValues = useMemo(
    () => mapPropertyAdToEditorForm(propertyAd, responsibleDefaultUserId),
    [propertyAd, responsibleDefaultUserId]
  );
  const formMethods = useForm<PropertyAdEditorFormValues>({
    defaultValues: initialValues,
    shouldUnregister: false,
  });

  useEffect(() => {
    formMethods.reset(initialValues);
  }, [formMethods, initialValues]);

  return (
    <PropertyAdEditorLayout
      actions={(media, onPhotoPublishErrorChange, finalization) => (
        <PropertyAdEditorActions
          {...finalization}
          media={media}
          mode='edit'
          onPhotoPublishErrorChange={onPhotoPublishErrorChange}
          propertyAd={propertyAd}
          responsibleDefaultUserId={responsibleDefaultUserId}
        />
      )}
      formMethods={formMethods}
      initialValues={initialValues}
      isAgency={isAgency}
      resetKey={`${propertyAd.id}:${propertyAd.updatedAt}`}
      resource={propertyAd}
    />
  );
};

export const PropertyAdEditor = (props: PropertyAdEditorProps) =>
  props.mode === 'edit' ? <EditLifecycle {...props} /> : <CreateLifecycle {...props} />;
